import "./style.scss";

import { Modal } from "@mui/material";
import StyledButton from "components/shared/StyledButton";
import { convertToRaw } from "draft-js";
import { stateToHTML } from "draft-js-export-html";
import { getCleanJSON } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import { Languages } from "languages";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useBlocker } from "react-router-dom";
import { putCompanySettings } from "store/settingsStore";
import type { RootState } from "store/store";
import { useAppDispatch } from "store/store";

import BlockModal from "../BlockModal";
import ControlFilter from "../ControlFilter";
import SaveButton from "../SaveButton";
import StyledTextField from "../StyledTextField/StyledTextField";
import EmailTemplates from "./EmailTemplates";
import MailEditor from "./MailEditor";
import BeneficiaryPortalEmailPreview from "./PreviewModal/beneficiaryPortalEmailHTML";

export type EmailTemplate = {
    id?: number;
    name: string;
    content: any;
    status: "original" | "edited";
};

const AccountEmail = () => {
    const dispatch = useAppDispatch();
    const [t] = useTranslation();
    const { companySettings } = useSelector((state: RootState) => state.settings);
    const { companyInfos } = useCompanySettings();
    const isPressOrAudiovisual = companyInfos?.publisherType
        ? ["audiovisual", "press"].includes(companyInfos?.publisherType)
        : false;
    const usesPortal = companySettings?.settings.filter((s) => s.name === "uses_portal");
    const { getCompanySettings } = useCompanySettings();
    const usesDocumentsTracking = !!getCompanySettings("uses_beneficiary_documents_tracking");
    const [emailTemplate, setEmailTemplate] = useState<EmailTemplate[]>([]);
    const [basicEmailsChanged, setBasicEmailsChanged] = useState(false);
    const [portalEmailTemplate, setPortalEmailTemplate] = useState<{
        id?: number;
        name: any;
        content: any;
    }>();
    const [portalEmailChanged, setPortalEmailChanged] = useState(false);
    const [portalEmailSubject, setPortalEmailSubject] = useState("");
    const [portalEmailHTML, setPortalEmailHTML] = useState("");
    const [openPreview, setOpenPreview] = useState(false);

    const [languageTab, setLanguageTab] = useState<"fr" | "en">(Languages()[0].value as any);

    const blocker = useBlocker(!emailTemplate.every((e) => e.status === "original") || portalEmailChanged);

    const hasLanguageChanged = useMemo(() => {
        return emailTemplate.length ? !emailTemplate[0].name.endsWith(languageTab) : false;
    }, [emailTemplate, languageTab]);

    // useEffect for basic emails
    useEffect(() => {
        if (companySettings?.settings && (!emailTemplate.length || hasLanguageChanged)) {
            const newArray: {
                id?: number;
                name: string;
                content: any;
                status: "original" | "edited";
            }[] = [];

            companySettings?.settings.forEach((s) => {
                const isReminderTemplate =
                    s.name.includes("documents_reminder_email_template") && usesDocumentsTracking;
                if (
                    (s.name.includes("statement_email_template") || isReminderTemplate) &&
                    s.name.endsWith(languageTab)
                ) {
                    newArray.push({ ...s, status: "original" });
                }
            });
            // Companies created before the tracking have no reminder template rows yet: offer them anyway
            const reminderName = `beneficiary_documents_reminder_email_template_${languageTab}`;
            if (usesDocumentsTracking && !newArray.some((template) => template.name === reminderName)) {
                newArray.push({
                    name: reminderName,
                    content: { subject: "", body: "", mailCC: [] },
                    status: "original",
                });
            }
            setEmailTemplate(newArray);
        }
    }, [companySettings, languageTab, usesDocumentsTracking]);

    // useEffect for portal email
    useEffect(() => {
        const template = structuredClone(
            Object.values(companySettings?.settings ?? {}).find(
                (template: { id?: number; name: any; content: any }) =>
                    template.name === `portal_welcome_email_template_${languageTab}`,
            ),
        );
        setPortalEmailTemplate(template);
        setPortalEmailSubject(getCleanJSON(template?.content)?.subject ?? "");
        setPortalEmailHTML(
            getCleanJSON(template?.content)
                ?.body?.replace(/&lt;/g, "<")
                .replace(/&gt;/g, ">"),
        );
        setPortalEmailChanged(false);
    }, [companySettings, languageTab]);

    const handleChangeSubject = (text: string) => {
        setPortalEmailChanged(true);
        setPortalEmailSubject(text ?? "");
        let newTemplate = portalEmailTemplate;
        let content =
            typeof newTemplate?.content === "string" ? JSON.parse(newTemplate?.content) : newTemplate?.content;
        content = { ...content, subject: text };
        newTemplate = {
            id: newTemplate?.id,
            name: newTemplate?.name,
            content: content,
        };
        setPortalEmailTemplate(newTemplate);
    };

    const handleChangePortal = (data: any) => {
        setPortalEmailChanged(true);
        const newTemplate = portalEmailTemplate;
        const subject =
            typeof newTemplate?.content === "string"
                ? JSON.parse(newTemplate?.content).subject
                : newTemplate?.content.subject;
        const content = {
            subject: subject,
            body: stateToHTML(data),
            blocks: convertToRaw(data),
        };
        newTemplate!.content = content;
        setPortalEmailTemplate(newTemplate);
        setPortalEmailHTML(newTemplate?.content?.body);
    };

    const handleSave = () => {
        if (basicEmailsChanged) {
            const sendArrayEmails = emailTemplate.map(({ status, ...data }) => {
                data.content = JSON.stringify(data.content);
                return {
                    id: data.id,
                    settingsName: data.name,
                    settingsValue: data.content,
                };
            });
            dispatch(putCompanySettings(sendArrayEmails)).then((resp) => {
                if (resp.meta.requestStatus === "fulfilled") {
                    const newArray = emailTemplate.map((e) => {
                        e.status = "original";
                        return e;
                    });
                    setEmailTemplate([...newArray]);
                }
            });
            setBasicEmailsChanged(false);
        }

        if (portalEmailChanged === true) {
            const sendArray = [
                {
                    id: portalEmailTemplate?.id,
                    settingsName: portalEmailTemplate?.name,
                    settingsValue: JSON.stringify(portalEmailTemplate?.content),
                },
            ];
            dispatch(putCompanySettings(sendArray));
            setPortalEmailChanged(false);
        }
        blocker.reset?.();
    };

    const languageOptions = [
        { value: Languages()[0].value, label: Languages()[0].description },
        { value: Languages()[1].value, label: Languages()[1].description },
    ];

    return (
        <div className="settings-email">
            <BlockModal blocker={blocker} />
            <div className="settings-email-header">
                <h1 className="member-list-title">{t("EmailSettings.Email")}</h1>
                <ControlFilter
                    filterOptions={languageOptions}
                    controlValue={languageTab}
                    onValueChange={(value) => setLanguageTab(value)}
                />
            </div>
            {emailTemplate.map((email, index) => {
                return (
                    <EmailTemplates
                        key={index}
                        company={companySettings}
                        email={email}
                        index={index}
                        isPressOrAudiovisual={isPressOrAudiovisual}
                        // handleEdit={(e: any, index: number, type: 'subject' | 'body') => handleEditField(e, index, type)}
                        emailTemplate={emailTemplate}
                        setEmailTemplate={setEmailTemplate}
                        onEmailsChanged={() => setBasicEmailsChanged(true)}
                        languageTab={languageTab}
                        hasLanguageChanged={hasLanguageChanged}
                    />
                );
            })}
            <div className="settings-email-footer">
                {(!emailTemplate.every((e) => e.status === "original") || portalEmailChanged) && (
                    <SaveButton onSave={handleSave} buttonText={t("EmailSettings.Save")} />
                )}
            </div>
            {usesPortal && (
                <div className="portal-welcome-email settings-email-content">
                    <Modal open={openPreview} onClose={() => setOpenPreview(false)}>
                        <BeneficiaryPortalEmailPreview
                            company={companySettings}
                            html={portalEmailHTML}
                            onClose={() => setOpenPreview(false)}
                            languageTab={languageTab}
                        />
                    </Modal>
                    <div className="portal-email-title">
                        <label className="settings-email-content-label">{t("EmailSettings.PortalMailTemplate")}</label>
                        <StyledButton buttonType="outlined" onClick={() => setOpenPreview(true)}>
                            {t("EmailSettings.Preview")}
                        </StyledButton>
                    </div>
                    <div className="settings-email-content-body portal-email-content">
                        <div style={{ padding: "0 0 10px 0", fontWeight: "500" }}>
                            {t("EmailSettings.Of")} : {companySettings?.name} via Crealo
                        </div>
                        <div style={{ display: "flex", alignItems: "center" }}>
                            <span className="label-object">{t("EmailSettings.Object")} :</span>
                            <StyledTextField
                                placeholder={t("EmailSettings.PortalObject", { lng: languageTab })}
                                onChange={(e: any) => handleChangeSubject(e.target.value)}
                                value={portalEmailSubject}
                            />
                        </div>
                    </div>
                    <MailEditor
                        onSave={(data: any) => handleChangePortal(data)}
                        hasChanged={portalEmailChanged}
                        onChange={(value: boolean) => setPortalEmailChanged(value)}
                        languageTab={languageTab}
                    />
                </div>
            )}
        </div>
    );
};

export default AccountEmail;
